/**
 * ContentGridItem
 *
 * Reusable grid cell for `UserMediaEntity` thumbnails. Used by any page that
 * renders a grid of user media (unlocked videos, content tag results, user
 * profile videos, etc.).
 *
 * Responsibilities:
 * - Render the thumbnail at the media's natural aspect ratio, centered, with
 *   the surface color showing through on letterbox/pillarbox edges.
 * - Skip thumbnail rendering while the entity is local OR while the
 *   server-side doc still reports isProcessing — the cloud function writes
 *   the thumbnail path before the file lands on the CDN, so requesting it
 *   would 404 and poison both CachedImage state and the browser HTTP cache.
 * - Show a "processing" overlay while the server is still transcoding.
 *
 * Click handling is delegated to the caller via `onClick(media)` — the
 * various pages have different navigation/sheet behaviors.
 */

import { useCallback } from 'react';
import { ContentPreview } from '@/presentation/feature/content/components/ContentPreview';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';

export interface ContentGridItemProps {
  media: UserMediaEntity;
  onClick: (media: UserMediaEntity) => void;
}

export const ContentGridItem = ({ media, onClick }: ContentGridItemProps) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  const handleClick = useCallback(() => {
    onClick(media);
  }, [media, onClick]);

  return (
    <div
      {...qaAttr({
        id: `content-grid-item-${media.id}`,
        status: media.isProcessing ? 'processing' : 'ready',
      })}
      className="cursor-pointer overflow-hidden relative flex flex-col items-center justify-center"
      style={{ backgroundColor: theme.surface }}
      onClick={handleClick}
    >
      {/* Sized to the media's natural aspect ratio — never distorted.
          Landscape: shorter than the portrait cell → centered with background above/below.
          Portrait: taller than the cell → centered, overflowing top/bottom, clipped by overflow-hidden.
          Skipped while the entity is local OR while the server-side doc still
          reports isProcessing — the cloud function writes the thumbnail path
          before the file lands on the CDN, so requesting it would 404 and
          poison both CachedImage state and the browser HTTP cache. */}
      {!media.isLocal && !media.isProcessing && (
        <div
          className="hover:opacity-80 transition-opacity"
          style={{ width: '100%', aspectRatio: media.aspectRatio }}
        >
          <ContentPreview
            thumbnailUrl={MediaUtils.getMediaThumbnailUrl(media)}
            alt={media.description || l10n.common.videoThumbnail}
          />
        </div>
      )}

      {/* Overlay for processing media */}
      {media.isProcessing && (
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ backgroundColor: `${theme.black}99` }}
        >
          <span style={{ color: theme.white, fontSize: responsive.font.sm }}>
            {l10n.common.processing}
          </span>
        </div>
      )}
    </div>
  );
};
